import React, { useCallback, useEffect, useState } from 'react';
import { PiEyeBold, PiEyeSlashBold } from 'react-icons/pi';
import { Box, Button, CircularProgress, FormControl, IconButton, Stack, TextField } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { signInWithEmail, signInWithEmailAndPassword } from '../utils/supabase';
import useToast from '../hooks/useToast';
import Logo from '../components/Logo';
import String from '../components/String';
import Layout from '../components/Layout';
import SetupStep from '../components/SetupStep';
import { HOSTNAME } from '../../config/config';
import LanguageSelector from '../components/LanguageSelector';
import PwaInstallButton from '../components/PwaInstallButton';
import FontSizeSelector from '../components/FontSizeSelector';
import { useLanguage } from '../contexts/language';
import SessionExpiredModal from '../components/SessionExpiredModal';

const SESSION_EXPIRED_PARAM = 'session_expired';

const getSearchParams = (search) => {
  if (!search) return null;
  try {
    const normalizedSearch = search.startsWith('?') ? search.substring(1) : search;
    return new URLSearchParams(normalizedSearch);
  } catch (error) {
    console.error('Unable to parse URL search params', error);
    return null;
  }
};

const hasSessionExpiredFlag = (search) => {
  const params = getSearchParams(search);
  if (!params) return false;
  return params.get(SESSION_EXPIRED_PARAM) === 'true';
};

const removeSessionExpiredFlag = (search) => {
  const params = getSearchParams(search);
  if (!params) return '';
  params.delete(SESSION_EXPIRED_PARAM);
  return params.toString();
};

const Login = ({ success, offlineMode }) => {
  const location = useLocation();
  const history = useHistory();
  const { pathname, search, hash } = location;

  const toast = useToast();
  const { content } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isOtp, setIsOtp] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [sessionExpiredModalOpen, setSessionExpiredModalOpen] = useState(false);

  useEffect(() => {
    setSessionExpiredModalOpen(hasSessionExpiredFlag(search));
  }, [search]);

  const handleSessionExpiredDismiss = useCallback(() => {
    setSessionExpiredModalOpen(false);
    try {
      const nextSearch = removeSessionExpiredFlag(search);
      history.replace({
        pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash,
      });
    } catch (error) {
      console.error('Unable to clean session expired flag from URL', error);
    }
  }, [history, pathname, search, hash]);

  const login = useCallback(async () => {
    if (!email || !password) return;

    setLoginLoading(true);

    const { error } = await signInWithEmailAndPassword({ email, password });
    if (error) {
      toast.error(content('wrong_credentials'));
      console.error(error);
    } else {
      success();
    }

    setLoginLoading(false);
  }, [email, password, content]);

  const getOtp = useCallback(async () => {
    if (!email) return;

    setOtpLoading(true);

    const { error } = await signInWithEmail({ email, redirectTo: HOSTNAME });
    if (error) {
      toast.error(content('invalid_email'));
      console.error(error);
    } else {
      toast.success(content('reset_password_instructions'));
    }

    setOtpLoading(false);
  }, [email, content]);

  const handleKeyUp = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
      login();
    }
  };

  const loading = loginLoading || otpLoading;

  return (
    <>
      <SessionExpiredModal open={sessionExpiredModalOpen} onClose={handleSessionExpiredDismiss} />
      <PwaInstallButton />
      <Layout
        fullscreen
        framed
        top={
          <>
            <Box position="absolute" top={0} left={0} textAlign="left">
              <FontSizeSelector color="secondary" />
            </Box>
            <Box position="absolute" top={0} right={0} textAlign="right">
              <LanguageSelector />
            </Box>
            <Box mt="8vh" textAlign="center">
              <Logo />
            </Box>
          </>
        }
      >
        <SetupStep
          form={
            <Stack spacing={1} mt={1} mb={2} width="100%">
              <FormControl variant="outlined" fullWidth>
                <TextField
                  value={email || ''}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  type="email"
                  label="Email address"
                  required
                  onKeyUp={handleKeyUp}
                  inputProps={{ 'aria-label': 'Email' }}
                  data-testid="login-email-input"
                />
              </FormControl>
              {!isOtp && (
                <Box style={{ position: 'relative' }}>
                  <FormControl variant="outlined" fullWidth>
                    <TextField
                      value={password || ''}
                      onChange={(e) => setPassword(e.currentTarget.value)}
                      type={showPassword ? 'text' : 'password'}
                      label="Password"
                      required
                      onKeyUp={handleKeyUp}
                      inputProps={{ 'aria-label': 'Password' }}
                      data-testid="login-password-input"
                    />
                  </FormControl>
                  <IconButton
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '10px',
                      transform: 'translate(0, -50%)',
                    }}
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? content('aria_hide_password') : content('aria_show_password')}
                  >
                    {showPassword ? <PiEyeSlashBold /> : <PiEyeBold />}
                  </IconButton>
                </Box>
              )}
              <Stack pt={3} spacing={3}>
                <Stack spacing={1}>
                  {!isOtp ? (
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={login}
                      disabled={!email || !password || loading}
                      endIcon={
                        loginLoading ? (
                          <CircularProgress style={{ marginLeft: 8, color: 'white' }} size="20px" />
                        ) : null
                      }
                      fullWidth
                      aria-label={content('login')}
                      data-testid="login-confirm-btn"
                    >
                      <String s="login" />
                    </Button>
                  ) : (
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={getOtp}
                      disabled={!email || loading}
                      endIcon={
                        otpLoading ? (
                          <CircularProgress style={{ marginLeft: 8, color: 'white' }} size="20px" />
                        ) : null
                      }
                      fullWidth
                      aria-label={content('aria_send_otp_email')}
                    >
                      <String s="send_email" />
                    </Button>
                  )}
                  <Button
                    color="primary"
                    variant="ghost"
                    onClick={() => setIsOtp((s) => !s)}
                    disabled={loading}
                    fullWidth
                    aria-label={
                      isOtp
                        ? content('aria_login_with_email_password')
                        : content('aria_login_with_email_only')
                    }
                  >
                    {isOtp ? <String s="login_with_password" /> : <String s="login_with_email" />}
                  </Button>
                </Stack>

                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={offlineMode}
                  fullWidth
                  aria-label={content('aria_offline_mode')}
                >
                  <String s="offline_experience" />
                </Button>
              </Stack>
            </Stack>
          }
          themeSwitcher
        />
      </Layout>
    </>
  );
};

export default Login;
